import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { PageHeader } from '@/components/PageHeader';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { legalDocumentService, type ActiveDocument } from '@/services/legalDocumentService';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [document, setDocument] = useState<ActiveDocument | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        // Fetch document with content from storage
        const { document: doc, content: docContent } = await legalDocumentService.getActiveDocumentWithContent('privacy_policy');
        setDocument(doc);
        setContent(docContent);
      } catch (error) {
        console.error('Error loading privacy policy:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();
  }, []);

  const renderMarkdown = (content: string) => {
    return content
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mb-4 text-primary">$1</h2>')
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-medium mt-4 mb-2">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^- (.*$)/gim, '<li class="ml-4 list-disc list-inside">$1</li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>')
      .replace(/\n\n/g, '</p><p class="mb-3">')
      .replace(/\n/g, '<br />');
  };

  if (isLoading) {
    return (
      <>
        <SEOHead 
          title="Privacy Policy - GrowthLens"
          description="Read GrowthLens's privacy policy to understand how we collect, use, and protect your personal information and assessment data."
          canonical="https://growthlens.in/privacy-policy"
        />
        <div className="min-h-screen bg-gradient-to-br from-background to-muted">
          <PageHeader
            heading="Privacy Policy"
            description="Loading..."
          />
          <div className="max-w-4xl mx-auto px-4 py-8">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Card className="card-enhanced">
              <CardContent className="p-8 space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-8 w-1/2 mt-6" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title="Privacy Policy - GrowthLens"
        description="Read GrowthLens's privacy policy to understand how we collect, use, and protect your personal information and assessment data."
        canonical="https://growthlens.in/privacy-policy"
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-muted">
        <PageHeader
          heading={document?.title || "Privacy Policy"}
          description={document ? `Last updated: ${document.version}` : ""}
        />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <Card className="card-enhanced hover-lift animate-fade-in transition-all duration-300 border-primary/10">
            <CardContent className="px-6 md:px-8 py-8">
              {content ? (
                <div 
                  className="prose prose-sm max-w-none text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMarkdown(content)) }}
                />
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Privacy policy not available. Please try again later.
                </p>
              )}
              
              {/* Contact link at the bottom */}
              <div className="mt-8 pt-6 border-t">
                <p className="text-sm text-muted-foreground">
                  If you have any questions about this privacy policy or our data practices, please contact us through our{' '}
                  <Link to="/contact" className="text-primary hover:underline">contact page</Link>.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
